import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Popper,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import React, { useEffect, useRef, useState } from 'react';

import { useAnalytics } from '@contexts/analyticsContext';

import { getBoolOrNone } from '@utils';

const disabledDimensionsInput =
  getBoolOrNone(
    process.env[
      'REACT_APP_FEATURE.ANALYTICS.FILTER.DISABLE_DIMENSIONS_FILTER_INPUT'
    ],
  ) ?? false;

const disabledMetricsInput =
  getBoolOrNone(
    process.env[
      'REACT_APP_FEATURE.ANALYTICS.FILTER.DISABLE_METRICS_FILTER_INPUT'
    ],
  ) ?? false;
const noop = () => {};

/**
 * The right-side panel for building a custom Google Analytics filter.
 * This component allows users to add and configure filter criteria.
 * @param {Object} props - Component props.
 * @param {boolean} props.isNewFilter - Indicates if the filter is new or an existing one.
 * @param {object} props.filterConfig - The current filter configuration object (including name, description, and criteria).
 * @param {function} props.onConfigChange - Callback to update the parent with changes.
 * @param {function} props.onSaveFilter - Callback to save the filter.
 * @param {function} props.onApplyFilter - Callback to apply the filter.
 */
function FilterCustomizationPanel({
  isNewFilter,
  filterConfig = {},
  onConfigChange = noop,
  onCancelFilter = noop,
  onApplyFilter = noop,
}) {
  const { metadata, metadataError, loadingMetadata } = useAnalytics();
  const [showDimensionPanel, setShowDimensionPanel] = useState(false);
  const [showMetricPanel, setShowMetricPanel] = useState(false);

  // States for the searchable select inputs
  const [dimensionSearchQuery, setDimensionSearchQuery] = useState('');
  const [metricSearchQuery, setMetricSearchQuery] = useState('');
  const [showDimensionDropdown, setShowDimensionDropdown] = useState(false);
  const [showMetricDropdown, setShowMetricDropdown] = useState(false);

  // Refs for anchoring the Popper components
  const dimensionAnchorRef = useRef(null);
  const metricAnchorRef = useRef(null);

  const [selectedDimensions, setSelectedDimensions] = useState(
    filterConfig?.dimensions?.map((d) => d.name) || [],
  );
  const [selectedMetrics, setSelectedMetrics] = useState(
    filterConfig?.metrics?.map((m) => m.name) || [],
  );
  const [filterCriteria, setFilterCriteria] = useState(
    filterConfig?.filterCriteria || [],
  );

  useEffect(() => {
    setSelectedDimensions(filterConfig?.dimensions?.map((d) => d.name) || []);
    setSelectedMetrics(filterConfig?.metrics?.map((m) => m.name) || []);
    setFilterCriteria(filterConfig?.filterCriteria || []);
  }, [filterConfig]);

  // Helper function to update the parent's filter configuration
  const triggerConfigChange = (
    newDimensions,
    newMetrics,
    newFilterCriteria,
  ) => {
    onConfigChange({
      ...filterConfig,
      dimensions: newDimensions.map((name) => ({ name })),
      metrics: newMetrics.map((name) => ({ name })),
      filterCriteria: newFilterCriteria,
    });
  };

  // Handler for adding/removing a dimension from the selected list
  const handleDimensionToggle = (dimensionApiName) => {
    let newDimensions;
    if (selectedDimensions.includes(dimensionApiName)) {
      newDimensions = selectedDimensions.filter(
        (name) => name !== dimensionApiName,
      );
    } else {
      newDimensions = [...selectedDimensions, dimensionApiName];
    }
    setSelectedDimensions(newDimensions);
    triggerConfigChange(newDimensions, selectedMetrics, filterCriteria);
  };

  // Handler for adding/removing a metric from the selected list
  const handleMetricToggle = (metricApiName) => {
    let newMetrics;
    if (selectedMetrics.includes(metricApiName)) {
      newMetrics = selectedMetrics.filter((name) => name !== metricApiName);
    } else {
      newMetrics = [...selectedMetrics, metricApiName];
    }
    setSelectedMetrics(newMetrics);
    triggerConfigChange(selectedDimensions, newMetrics, filterCriteria);
  };

  // Handler for removing a chip from the selected list
  const handleChipDelete = (type, apiName) => () => {
    if (type === 'dimension') {
      handleDimensionToggle(apiName);
    } else {
      handleMetricToggle(apiName);
    }
  };

  const handleAddCriterion = (type, fieldName) => {
    // Set a default operator based on the type
    const defaultOperator = type === 'dimension' ? 'EQUALS' : 'GREATER_THAN';

    onConfigChange({
      ...(filterConfig || {}),
      filterCriteria: [
        ...(filterConfig || {}).filterCriteria,
        { type, fieldName, operator: defaultOperator, value: '' },
      ],
    });
  };

  const handleRemoveCriterion = (indexToRemove) => {
    onConfigChange({
      ...(filterConfig || {}),
      filterCriteria: (filterConfig || {}).filterCriteria.filter(
        (_, index) => index !== indexToRemove,
      ),
    });
  };

  const handleUpdateCriterion = (indexToUpdate, field, value) => {
    onConfigChange({
      ...(filterConfig || {}),
      filterCriteria: (filterConfig || {}).filterCriteria.map(
        (criterion, index) =>
          index === indexToUpdate
            ? { ...criterion, [field]: value }
            : criterion,
      ),
    });
  };

  const handleSelectDimension = (dimension) => {
    handleAddCriterion('dimension', dimension.apiName);
    setShowDimensionPanel(false);
    setDimensionSearchQuery('');
  };

  const handleSelectMetric = (metric) => {
    handleAddCriterion('metric', metric.apiName);
    setShowMetricPanel(false);
    setMetricSearchQuery('');
  };

  const filteredDimensions =
    metadata?.dimensions?.filter?.(
      (d) =>
        d.uiName.toLowerCase().includes(dimensionSearchQuery.toLowerCase()) ||
        d.apiName.toLowerCase().includes(dimensionSearchQuery.toLowerCase()),
    ) || [];

  const filteredMetrics =
    metadata?.metrics?.filter?.(
      (m) =>
        m.uiName.toLowerCase().includes(metricSearchQuery.toLowerCase()) ||
        m.apiName.toLowerCase().includes(metricSearchQuery.toLowerCase()),
    ) || [];

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {loadingMetadata ? (
        <Box display='flex' justifyContent='center' py={4}>
          <CircularProgress />
        </Box>
      ) : metadataError ? (
        <Alert severity='error'>{metadataError}</Alert>
      ) : (
        <>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Report Data
          </Typography>

          <FormHelperText sx={{ mb: 2 }}>
            Dimensions describe your data; metrics provide measurements.
            Incorrect parameters may cause errors.
          </FormHelperText>

          {/* Searchable Multi-select for Dimensions */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id='dimensions-select-label' shrink>
              {`Dimensions${disabledDimensionsInput ? ' (disabled)' : ''}`}
            </InputLabel>
            <ClickAwayListener
              onClickAway={() => setShowDimensionDropdown(false)}
            >
              <Box ref={dimensionAnchorRef}>
                <OutlinedInput
                  disabled={disabledDimensionsInput}
                  id='select-multiple-chip-dimensions'
                  label={`Dimensions${
                    disabledDimensionsInput ? ' (disabled)' : ''
                  }`}
                  value={dimensionSearchQuery}
                  onChange={
                    disabledDimensionsInput
                      ? noop
                      : (e) => setDimensionSearchQuery(e.target.value)
                  }
                  onFocus={
                    disabledDimensionsInput
                      ? noop
                      : () => setShowDimensionDropdown(true)
                  }
                  fullWidth
                  startAdornment={
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        p: '12px 4px 8px 4px',
                      }}
                    >
                      {selectedDimensions.map((value) => (
                        <Chip
                          key={value}
                          label={
                            metadata.dimensions.find((d) => d.apiName === value)
                              ?.uiName
                          }
                          title={
                            metadata.dimensions.find((d) => d.apiName === value)
                              ?.uiName
                          }
                          sx={{
                            height: 'auto',
                            borderRadius: '4px',
                            '& .MuiChip-label': {
                              display: 'block',
                              lineHeight: '1.3em',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal',
                              textOverflow: 'clip',
                            },
                          }}
                          onDelete={
                            disabledDimensionsInput
                              ? noop
                              : handleChipDelete('dimension', value)
                          }
                          size='small'
                        />
                      ))}
                    </Box>
                  }
                  sx={{
                    '& .MuiInputBase-input': {
                      minHeight: '20px',
                      minWidth: '18%',
                      padding: '10px 14px',
                      borderRadius: '4px',
                    },
                    borderRadius: '4px',
                  }}
                />
                <Popper
                  open={showDimensionDropdown}
                  anchorEl={dimensionAnchorRef.current}
                  placement='bottom-start'
                  sx={{
                    width: dimensionAnchorRef.current?.offsetWidth,
                    zIndex: 1350,
                  }}
                >
                  <Paper sx={{ maxHeight: 224, overflow: 'auto' }}>
                    <List dense>
                      {filteredDimensions.length > 0 ? (
                        filteredDimensions.map((d) => (
                          <ListItem
                            key={d.apiName}
                            onClick={() => handleDimensionToggle(d.apiName)}
                            selected={selectedDimensions.includes(d.apiName)}
                            button
                          >
                            <ListItemText
                              primary={d.uiName}
                              secondary={d.apiName}
                            />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText secondary='No dimensions found.' />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </Popper>
              </Box>
            </ClickAwayListener>
          </FormControl>

          {/* Searchable Multi-select for Metrics */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id='metrics-select-label' shrink>
              {`Metrics${disabledMetricsInput ? ' (disabled)' : ''}`}
            </InputLabel>
            <ClickAwayListener onClickAway={() => setShowMetricDropdown(false)}>
              <Box ref={metricAnchorRef}>
                <OutlinedInput
                  disabled={disabledMetricsInput}
                  id='select-multiple-chip-metrics'
                  label={`Metrics${disabledMetricsInput ? ' (disabled)' : ''}`}
                  value={metricSearchQuery}
                  onChange={
                    disabledMetricsInput
                      ? noop
                      : (e) => setMetricSearchQuery(e.target.value)
                  }
                  onFocus={
                    disabledMetricsInput
                      ? noop
                      : () => setShowMetricDropdown(true)
                  }
                  fullWidth
                  startAdornment={
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        p: '12px 4px 8px 4px',
                      }}
                    >
                      {selectedMetrics.map((value) => (
                        <Chip
                          key={value}
                          label={
                            metadata.metrics.find((m) => m.apiName === value)
                              ?.uiName
                          }
                          title={
                            metadata.metrics.find((m) => m.apiName === value)
                              ?.uiName
                          }
                          sx={{
                            height: 'auto',
                            borderRadius: '4px',
                            '& .MuiChip-label': {
                              display: 'block',
                              lineHeight: '1.3em',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal',
                              textOverflow: 'clip',
                            },
                          }}
                          onDelete={
                            disabledMetricsInput
                              ? noop
                              : handleChipDelete('metric', value)
                          }
                          size='small'
                        />
                      ))}
                    </Box>
                  }
                  sx={{
                    '& .MuiInputBase-input': {
                      minHeight: '20px',
                      minWidth: '18%',
                      padding: '10px 14px',
                      borderRadius: '4px',
                    },
                    borderRadius: '4px',
                  }}
                />
                <Popper
                  open={showMetricDropdown}
                  anchorEl={metricAnchorRef.current}
                  placement='bottom-start'
                  sx={{
                    width: metricAnchorRef.current?.offsetWidth,
                    zIndex: 1350,
                  }}
                >
                  <Paper sx={{ maxHeight: 224, overflow: 'auto' }}>
                    <List dense>
                      {filteredMetrics.length > 0 ? (
                        filteredMetrics.map((m) => (
                          <ListItem
                            key={m.apiName}
                            onClick={() => handleMetricToggle(m.apiName)}
                            selected={selectedMetrics.includes(m.apiName)}
                            button
                          >
                            <ListItemText
                              primary={m.uiName}
                              secondary={m.apiName}
                            />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText secondary='No metrics found.' />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </Popper>
              </Box>
            </ClickAwayListener>
          </FormControl>

          {/* Dimensions criteria Selection Panel */}
          {showDimensionPanel && (
            <Box sx={{ mb: 2 }}>
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
              >
                <Typography variant='subtitle1'>Select Dimension</Typography>
                <IconButton onClick={() => setShowDimensionPanel(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                size='small'
                label='Search dimensions'
                value={dimensionSearchQuery}
                onChange={(e) => setDimensionSearchQuery(e.target.value)}
                sx={{ my: 2 }}
              />
              <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {filteredDimensions.length > 0 ? (
                    filteredDimensions.map((dimension) => (
                      <ListItem
                        button
                        key={dimension.apiName}
                        onClick={() => handleSelectDimension(dimension)}
                      >
                        <ListItemText
                          primary={dimension.uiName}
                          secondary={dimension.description}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText secondary='No dimensions found.' />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Box>
          )}

          {/* Metric criteria Selection Panel */}
          {showMetricPanel && (
            <Box sx={{ mb: 2 }}>
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
              >
                <Typography variant='subtitle1'>Select Metric</Typography>
                <IconButton onClick={() => setShowMetricPanel(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                size='small'
                label='Search metrics'
                value={metricSearchQuery}
                onChange={(e) => setMetricSearchQuery(e.target.value)}
                sx={{ my: 2 }}
              />
              <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {filteredMetrics.length > 0 ? (
                    filteredMetrics.map((metric) => (
                      <ListItem
                        button
                        key={metric.apiName}
                        onClick={() => handleSelectMetric(metric)}
                      >
                        <ListItemText
                          primary={metric.uiName}
                          secondary={metric.description}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText secondary='No metrics found.' />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Box>
          )}

          {/* Filter criteria list */}
          <Typography variant='h6' sx={{ mt: 3, mb: 1 }}>
            Report Filter
          </Typography>
          <FormHelperText sx={{ mb: 2 }}>
            Filter must be based on a selected dimension or metric above.
            Incorrect parameters may cause errors.
          </FormHelperText>
          {filterConfig?.filterCriteria &&
            filterConfig.filterCriteria.map((criterion, index) => (
              <Paper
                key={index}
                elevation={1}
                sx={{ p: 2, mb: 2, position: 'relative' }}
              >
                <IconButton
                  aria-label='delete'
                  size='small'
                  onClick={() => handleRemoveCriterion(index)}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
                <Typography variant='subtitle2' mb={1}>
                  {criterion.type === 'dimension' ? 'Dimension' : 'Metric'}:{' '}
                  <Typography component='span'>
                    {
                      (criterion.type === 'dimension'
                        ? metadata.dimensions
                        : metadata.metrics
                      ).find((item) => item.apiName === criterion.fieldName)
                        ?.uiName
                    }
                  </Typography>
                </Typography>
                <Box mt={2}>
                  <FormControl size='small' fullWidth>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={criterion.operator}
                      label='Operator'
                      onChange={(e) =>
                        handleUpdateCriterion(index, 'operator', e.target.value)
                      }
                    >
                      {criterion.type === 'dimension'
                        ? [
                            <MenuItem
                              key='criterion-dimension-equals'
                              value='EQUALS'
                            >
                              Equals
                            </MenuItem>,
                            <MenuItem
                              key='criterion-dimension-contains'
                              value='CONTAINS'
                            >
                              Contains
                            </MenuItem>,
                            <MenuItem
                              key='criterion-dimension-begins_with'
                              value='BEGINS_WITH'
                            >
                              Begins with
                            </MenuItem>,
                            <MenuItem
                              key='criterion-dim-ends_with'
                              value='ENDS_WITH'
                            >
                              Ends with
                            </MenuItem>,
                            <MenuItem
                              key='criterion-dim-matches_regex'
                              value='MATCHES_REGEX'
                            >
                              Matches regex
                            </MenuItem>,
                            <MenuItem
                              key='criterion-dim-in_list'
                              value='IN_LIST'
                            >
                              In list (comma-separated)
                            </MenuItem>,
                          ]
                        : [
                            <MenuItem
                              key='criterion-metric-equals'
                              value='EQUALS'
                            >
                              Equals
                            </MenuItem>,
                            <MenuItem
                              key='criterion-metric-greater_than'
                              value='GREATER_THAN'
                            >
                              Greater Than
                            </MenuItem>,
                            <MenuItem
                              key='criterion-metric-less_than'
                              value='LESS_THAN'
                            >
                              Less Than
                            </MenuItem>,
                            <MenuItem
                              key='criterion-metric-greater_than_or_equal'
                              value='GREATER_THAN_OR_EQUAL'
                            >
                              Greater Than or Equal
                            </MenuItem>,
                            <MenuItem
                              key='criterion-metric-less_than_or_equals'
                              value='LESS_THAN_OR_EQUAL'
                            >
                              Less Than or Equal
                            </MenuItem>,
                          ]}
                    </Select>
                  </FormControl>
                </Box>
                <Box mt={2}>
                  <TextField
                    size='small'
                    fullWidth
                    label='Value'
                    value={criterion.value}
                    onChange={(e) =>
                      handleUpdateCriterion(index, 'value', e.target.value)
                    }
                  />
                </Box>
              </Paper>
            ))}

          {/* Buttons for adding filters */}
          <Box display='flex' gap={2} mt={3} sx={{ flexDirection: 'column' }}>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={() => {
                setShowMetricPanel(false);
                setShowDimensionPanel(true);
              }}
            >
              Add Dimension Criteria
            </Button>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={() => {
                setShowDimensionPanel(false);
                setShowMetricPanel(true);
              }}
            >
              Add Metric Criteria
            </Button>
          </Box>

          {/* Action buttons */}
          <Box mt='auto' pt={3} display='flex' gap={2}>
            <Button variant='outlined' onClick={onCancelFilter} fullWidth>
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={onApplyFilter}
              fullWidth
              disabled={
                selectedDimensions.length === 0 && selectedMetrics.length === 0
              }
            >
              Apply Filter
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default FilterCustomizationPanel;
